import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import QRCode from "qrcode";

/**
 * QR 코드 — <b>브라우저에서 직접 생성한다.</b>
 *
 * <h3>외부 QR 서비스를 쓰지 않는 이유</h3>
 * <p>이 저장소에는 {@code https://api.qrserver.com/...?data=<값>} 형태로 QR 을 만들던 코드가 있었다.
 * 그 방식은 <b>QR 에 담을 값을 외부 사업자에게 그대로 전송</b>한다. 참가자 UUID 든 푸시 페어링
 * 코드든, 값 자체가 식별자·자격증명이라 밖으로 나가면 안 된다. 페어링 코드의 경우 유출되면
 * 남의 폰이 내 계정에 구독 등록돼 내 알림을 받는다.
 *
 * <p>부수 효과도 있다 — 외부 서비스가 죽으면 QR 이 안 뜨고, 사내망에서 그 도메인이 막히면
 * 아무 설명 없이 깨진 이미지만 남는다. 로컬 생성은 그 의존이 전부 사라진다.
 *
 * <h3>왜 canvas 가 아니라 data URL 인가</h3>
 * <p>{@code toDataURL} 은 결과가 문자열이라 {@code <img>} 로 그리면 끝이고, MUI 스타일·확대·
 * 인쇄가 그대로 먹는다. canvas 는 ref 생명주기를 신경 써야 하고 리렌더마다 다시 그려야 한다.
 *
 * @param value      QR 에 담을 문자열
 * @param size       한 변 픽셀 (기본 220)
 * @param alt        대체 텍스트
 */
export default function QrCode({ value, size = 220, alt = "QR 코드", sx }) {
    const [dataUrl, setDataUrl] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        if (!value) {
            setDataUrl(null);
            return undefined;
        }
        QRCode.toDataURL(String(value), {
            width: size,
            margin: 2,
            // 오류 정정 레벨 M — 화면 QR 을 카메라로 찍는 용도에 충분하다.
            // H 로 올리면 같은 크기에서 셀이 작아져 오히려 인식률이 떨어진다.
            errorCorrectionLevel: "M",
            color: { dark: "#111827", light: "#FFFFFF" },
        })
            .then((url) => { if (!cancelled) { setDataUrl(url); setError(null); } })
            .catch((e) => { if (!cancelled) { setError(e); setDataUrl(null); } });

        return () => { cancelled = true; };
    }, [value, size]);

    if (error) {
        return (
            <Box sx={{ width: size, height: size, display: "flex", alignItems: "center",
                       justifyContent: "center", border: "1px solid #e2e8f0", borderRadius: 2, ...sx }}>
                <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>QR 생성 실패</Typography>
            </Box>
        );
    }

    if (!dataUrl) {
        // 생성은 수 밀리초지만, 자리를 미리 잡아 두어야 QR 이 나타날 때 레이아웃이 튀지 않는다.
        return <Box sx={{ width: size, height: size, bgcolor: "#f1f5f9", borderRadius: 2, ...sx }} />;
    }

    return (
        <Box component="img" src={dataUrl} alt={alt}
             sx={{ width: size, height: size, display: "block", borderRadius: 2, ...sx }} />
    );
}
